import { useEffect, useRef, useState } from 'react'
import Spinner from './Spinner'
import { CATEGORY_OPTIONS, ENVIRONMENTS, LOCATION_OPTIONS, locationLabel, modelsForCategory } from '../constants'

// Free-text fields: compared trimmed, and saved as null when blank.
const TEXT_FIELDS = ['name', 'notes']

function formFrom(device) {
  return {
    name: device.name ?? '',
    category: device.category,
    model: device.model,
    environment: device.environment,
    location: device.location ?? 'UNKNOWN',
    is_virtual: !!device.is_virtual,
    notes: device.notes ?? '',
  }
}

// Edit a device's fields (not its MDI — a different MDI is a different device).
// onSave(changes) receives only the fields that differ and should throw on error;
// the dialog stays open, with the form intact, if it does.
export default function EditDeviceDialog({ device, open, onClose, onSave }) {
  const ref = useRef(null)
  const [form, setForm] = useState(() => formFrom(device))
  const [saving, setSaving] = useState(false)

  // Controlled open state drives the native <dialog>; backdrop + Escape come from showModal().
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) { setForm(formFrom(device)); el.showModal() }
    else if (!open && el.open) el.close()
  }, [open, device])

  function setField(key, value) {
    setForm(f => {
      const next = { ...f, [key]: value }
      // Models depend on the category — reset a model that's no longer valid.
      if (key === 'category' && !modelsForCategory(value).includes(f.model)) next.model = ''
      return next
    })
  }

  const original = formFrom(device)
  const norm = (k, v) => (TEXT_FIELDS.includes(k) ? v.trim() : v)
  const changes = Object.fromEntries(
    Object.entries(form)
      .filter(([k, v]) => norm(k, v) !== norm(k, original[k]))
      .map(([k, v]) => [k, TEXT_FIELDS.includes(k) ? (v.trim() || null) : v]),
  )
  const envChanged = 'environment' in changes
  const identityChanged = envChanged || 'category' in changes || 'model' in changes

  async function submit(e) {
    e.preventDefault()
    if (Object.keys(changes).length === 0) { onClose(); return }
    setSaving(true)
    try {
      await onSave(changes)
    } catch {
      /* onSave reports the error; keep the dialog open */
    } finally {
      setSaving(false)
    }
  }

  return (
    <dialog
      ref={ref}
      className="modal"
      aria-labelledby="edit-device-title"
      onCancel={e => { if (saving) e.preventDefault() }}
      onClose={() => { if (open) onClose() }}
    >
      <div className="modal-box w-full max-w-lg">
        <h3 id="edit-device-title" className="font-bold text-lg mb-1">Edit device</h3>
        <p className="font-mono text-sm opacity-60 mb-4">{device.mdi}</p>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="form-control flex flex-col">
            <div className="label"><span className="label-text">Name</span></div>
            <input
              className="input input-bordered input-sm w-full"
              placeholder="No name"
              value={form.name}
              onChange={e => setField('name', e.target.value)}
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="form-control flex flex-col">
              <div className="label"><span className="label-text">Category</span></div>
              <select
                className="select select-bordered select-sm"
                value={form.category}
                onChange={e => setField('category', e.target.value)}
                required
              >
                {CATEGORY_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="form-control flex flex-col">
              <div className="label"><span className="label-text">Model</span></div>
              <select
                className="select select-bordered select-sm"
                value={form.model}
                onChange={e => setField('model', e.target.value)}
                required
              >
                <option value="">Select…</option>
                {modelsForCategory(form.category).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="form-control flex flex-col">
              <div className="label"><span className="label-text">Environment</span></div>
              <select
                className="select select-bordered select-sm"
                value={form.environment}
                onChange={e => setField('environment', e.target.value)}
              >
                {ENVIRONMENTS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="form-control flex flex-col">
              <div className="label"><span className="label-text">Location</span></div>
              <select
                className="select select-bordered select-sm"
                value={form.location}
                onChange={e => setField('location', e.target.value)}
              >
                {LOCATION_OPTIONS.map(v => <option key={v} value={v}>{locationLabel(v)}</option>)}
              </select>
            </label>
          </div>

          <label className="form-control flex flex-col">
            <div className="label"><span className="label-text">Notes</span></div>
            <textarea
              className="textarea textarea-bordered textarea-sm w-full"
              rows={2}
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
            />
          </label>

          <label className="flex items-center gap-2 cursor-pointer mt-1">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={form.is_virtual}
              onChange={e => setField('is_virtual', e.target.checked)}
            />
            <span className="label-text">Virtual device (not physical hardware)</span>
          </label>

          {identityChanged && (
            <div role="note" className="text-xs opacity-70 bg-base-200 rounded-lg p-3">
              {envChanged && (
                <p>
                  Moving to {form.environment} clears the stored cloud data and re-fetches it
                  from {form.environment}.
                </p>
              )}
              <p>
                Environment, category and model can&apos;t be changed while the device is reserved
                or in a bundle.
              </p>
            </div>
          )}

          <div className="modal-action mt-2">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={saving || !form.model}
              aria-busy={saving}
            >
              {saving ? <Spinner size="xs" /> : 'Save'}
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button disabled={saving} aria-label="Close">close</button>
      </form>
    </dialog>
  )
}
